/* eslint-disable no-restricted-imports */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import {T} from '../../../common/T'
import {ACTION_BUTTON_IDS, useActionButtonCommonErrors} from '../utils'
import IconHourGlass from '@static/images/icon_hourglass.svg'
import IconPassTurn from '@static/images/icon_pass_turn.svg'
import {useHotkeys} from 'react-hotkeys-hook'
import {useGameStore} from '../../../store/GameStoreContext'
import clsx from 'clsx'
import styles from '../UIGameActionButtonContainer.module.scss'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
// eslint-disable-next-line no-restricted-imports
import {StorageWrapper} from '../../../../../../code/client/shared/scripts/StorageWrapper'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext'
import {useEndTurnSuggestion} from './useEndTurnSuggestion'
import {GameAction, ModeSettingEnum} from '@colonist/game-base-types'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator'
import {UIGameActionButtonTurnToggleCount} from './UIGameActionButtonTurnToggleCount'

const SPACEBAR_SHORTCUT_DISABLED_AT_TURN_START_MS = 500

export const UIGameActionButtonTurn = ({
    className,
    hideTooltip = false,
}: {
    className?: string
    hideTooltip?: boolean
}) => {
    const SPAM_PREVENTION_MS = 600
    const [diceRolledTime, setDiceRolledTime] = useState(0)
    const lastClickTimeRef = useRef(0)
    const {sendClickedPassTurn} = useUIGameSocketContext()
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const diceState = useGameStore((state) => state.gameState.diceState)
    const myColor = useGameStore((state) => state.myColor)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const isHidden = beginnerActionState?.hiddenUIElements.includes(HiddenUIElement.EndTurnActionButton)
    const isNotBeginnerPassedTurnAction = beginnerActionState != null && beginnerActionState?.type !== GameAction.PassedTurn
    const currentStateValidator = useCurrentStateValidator()
    const hasPlayerToggledEndTurn = currentStateValidator.hasPlayerToggledEndTurn(myColor)
    const disabled = (hasCommonErrors || isHidden || isNotBeginnerPassedTurnAction) && !hasPlayerToggledEndTurn
    const {shouldShowSuggestion} = useEndTurnSuggestion()
    const setIsShowTradePanel = useGameStore((state) => state.gameTrade.setIsShowTradePanel)
    const isColonistRush = useGameStore((state) => state.gameSettings.modeSetting === ModeSettingEnum.ColonistRush4P)

    const onClick = useCallback(() => {
        if(disabled) return

        const now = Date.now()
        const shouldPreventClick = now - lastClickTimeRef.current < SPAM_PREVENTION_MS
        if(shouldPreventClick) return

        lastClickTimeRef.current = now
        sendClickedPassTurn()
        setIsShowTradePanel(false)
    }, [disabled, sendClickedPassTurn, setIsShowTradePanel])

    useEffect(() => {
        if(diceState.diceThrown) {
            setDiceRolledTime(Date.now())
        }
    }, [diceState.diceThrown])

    const isListeningForKeyPress = useCallback(() => {
        if(!StorageWrapper.getKeyboardShortcuts()) return false
        if(disabled) return false

        const msSinceDiceRolled = Date.now() - diceRolledTime
        if(msSinceDiceRolled < SPACEBAR_SHORTCUT_DISABLED_AT_TURN_START_MS) return false
        return true
    }, [disabled, diceRolledTime])

    useHotkeys('Space', () => {
        if(isListeningForKeyPress()) onClick?.()
    }, [isListeningForKeyPress, onClick])

    const icon = useMemo(() => {
        if(isColonistRush) return IconPassTurn
        return disabled ? IconHourGlass : IconPassTurn
    }, [isColonistRush, disabled])

    const soundOnClick = useMemo(() => {
        if(isColonistRush) return undefined // TODO add custom sound for Colonist Rush
        return undefined
    }, [isColonistRush])

    const isMechanicTurnColonistRushEnabled =
        useGameStore((state) => state.gameState.experimentalMechanicState?.mechanicTurnColonistRushState !== undefined)
    const playerCount = isMechanicTurnColonistRushEnabled ? <UIGameActionButtonTurnToggleCount /> : undefined

    const tooltipTitle = useMemo(() => {
        if(hideTooltip) return undefined
        if(isMechanicTurnColonistRushEnabled) {
            return <T id='strings:game.tooltips.endTurnRush'/>
        }
        return <T id='strings:game.tooltips.endTurn'/>
    }, [hideTooltip, isMechanicTurnColonistRushEnabled])

    return <UIGameActionButton
        className={clsx(className, {
            [styles.hidden]: isHidden,
        })}
        id={ACTION_BUTTON_IDS.passTurn}
        onClick={onClick}
        soundOnClick={soundOnClick}
        disabled={disabled}
        helperText={disabled ? errorHelperText : undefined}
        iconPath={icon}
        highlight={hasPlayerToggledEndTurn}
        tooltipTitle={tooltipTitle}
        scalePulseAnimation={shouldShowSuggestion}
        count={playerCount}
        countColor={myColor}
    />
}
